import { useState } from "react";
import { Building2, CheckCircle2, Copy, Download, FileText, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { fmtDate } from "@/components/landing/CompanyLookup";
import { isNip } from "@/lib/validate";

const field = "w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-teal/40";
const lbl = "text-xs font-semibold text-muted-foreground mb-1.5 block";

export function ProformaGenerator({ context = "admin" }) {
  const [f, setF] = useState({
    nip: "", company: "", registered_address: "", description: "", amount_net: "",
    vat_rate: 23, email: "", include_payu: true, send_client_email: false,
  });
  const [gus, setGus] = useState({ loading: false, data: null });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [manual, setManual] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const companyLocked = !manual;

  const runGus = async () => {
    const digits = (f.nip || "").replace(/\D/g, "");
    if (!isNip(digits)) { toast.error("Podaj poprawny NIP (10 cyfr, suma kontrolna)."); return; }
    setGus({ loading: true, data: null });
    try {
      const r = await api.get(`/company/${digits}`);
      if (!r.data.found) { setGus({ loading: false, data: null }); setManual(true); toast.error("Nie znaleziono firmy w GUS — wpisz dane ręcznie."); return; }
      setF((s) => ({ ...s, company: r.data.name || s.company, registered_address: r.data.address || s.registered_address }));
      setManual(false);
      setGus({ loading: false, data: r.data });
    } catch (e) {
      setGus({ loading: false, data: null });
      setManual(true);
      toast.error(e.response?.data?.detail || "Rejestr GUS niedostępny — wpisz dane ręcznie.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if ((f.nip || "").trim() && !isNip(f.nip)) { toast.error("Podaj poprawny NIP nabywcy lub zostaw puste."); return; }
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/internal/proforma", f);
      setResult(r.data);
      toast.success("Proforma wygenerowana.");
    } catch (er) {
      const d = er.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Nie udało się wygenerować proformy.");
    } finally { setBusy(false); }
  };

  const copyPay = () => { navigator.clipboard?.writeText(result.pay_url); toast.success("Link PayU skopiowany."); };

  return (
    <div data-testid={`proforma-generator-${context}`} className="bg-card border border-border rounded-2xl p-5 sm:p-7">
      <div className="flex items-center gap-2.5 mb-1">
        <span className="w-9 h-9 rounded-xl bg-brand-teal text-white flex items-center justify-center shrink-0"><FileText size={18} /></span>
        <h3 className="font-display font-bold text-lg">Szybka proforma (dowolna usługa)</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-6 leading-relaxed">Wystaw samą fakturę proforma: pobierz dane nabywcy z GUS (lub wpisz ręcznie), podaj własny opis usługi i kwotę netto. VAT 23% doliczany automatycznie. Opcjonalnie dołącz link PayU i wyślij PDF do klienta.</p>

      <form onSubmit={submit} className="space-y-5" data-testid="proforma-generator-form">
        <div>
          <label className={lbl}>NIP nabywcy</label>
          <div className="flex gap-2">
            <Input data-testid="pf-nip" value={f.nip} onChange={(e) => set("nip", e.target.value)} placeholder="10 cyfr" className="bg-background" />
            <button type="button" data-testid="pf-gus-button" onClick={runGus} disabled={gus.loading} className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-lg px-3 hover:bg-muted transition-colors disabled:opacity-50">
              {gus.loading ? <Loader2 size={13} className="animate-spin" /> : <Building2 size={13} />} Pobierz z GUS
            </button>
          </div>
          {gus.data && (
            <div data-testid="pf-gus-result" className="mt-2 rounded-lg border border-brand-teal/25 bg-brand-teal/5 px-3 py-2 text-xs leading-relaxed">
              <p className="inline-flex items-start gap-1.5 font-semibold text-foreground"><CheckCircle2 size={13} className="text-brand-teal shrink-0 mt-0.5" /><span>Pobrano z GUS: {gus.data.name}</span></p>
              <ul className="mt-1 space-y-0.5 text-muted-foreground pl-[18px]">
                {gus.data.vat_status && <li>VAT: {gus.data.vat_status}</li>}
                {gus.data.start_date && <li>Działalność od: {fmtDate(gus.data.start_date)}</li>}
                {(gus.data.vat_checked || gus.data.gus_checked) && <li className="text-brand-teal/90">Dane z rejestru na dzień {fmtDate(gus.data.vat_checked) || fmtDate(new Date().toISOString())}</li>}
                {gus.data.ended && <li className="text-brand-red font-semibold">Uwaga: działalność wykreślona lub zakończona</li>}
              </ul>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-semibold text-muted-foreground">Nazwa nabywcy</label>
              <button type="button" data-testid="pf-manual-toggle" onClick={() => setManual((v) => !v)} className="text-[11px] font-semibold text-brand-teal hover:text-brand-tealDark transition-colors">
                {manual ? "Zablokuj (dane z GUS)" : "Wpisz dane ręcznie"}
              </button>
            </div>
            <Input data-testid="pf-company" value={f.company} onChange={(e) => set("company", e.target.value)} readOnly={companyLocked} required className={`bg-background ${companyLocked ? "opacity-80 cursor-not-allowed" : ""}`} />
          </div>
          <div>
            <label className={lbl}>Adres nabywcy</label>
            <Input data-testid="pf-address" value={f.registered_address} onChange={(e) => set("registered_address", e.target.value)} readOnly={companyLocked} className={`bg-background ${companyLocked ? "opacity-80 cursor-not-allowed" : ""}`} />
          </div>
        </div>

        <div>
          <label className={lbl}>Opis usługi (pozycja na proformie)</label>
          <Textarea data-testid="pf-description" rows={2} value={f.description} onChange={(e) => set("description", e.target.value)} required placeholder="np. Doradztwo księgowe — październik 2026" className="bg-background" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={lbl}>Kwota netto (PLN)</label>
            <Input data-testid="pf-amount" value={f.amount_net} onChange={(e) => set("amount_net", e.target.value)} required placeholder="np. 1000" className="bg-background" />
          </div>
          <div>
            <label className={lbl}>Stawka VAT (%)</label>
            <select data-testid="pf-vat" className={field} value={f.vat_rate} onChange={(e) => set("vat_rate", Number(e.target.value))}>
              {[23, 8, 5, 0].map((v) => <option key={v} value={v}>{v}%</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>E-mail klienta (opcjonalnie)</label>
            <Input data-testid="pf-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="wymagany dla PayU / wysyłki" className="bg-background" />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 border-t border-border pt-4">
          <label className="flex items-center gap-2.5 text-sm cursor-pointer">
            <Switch data-testid="pf-payu" checked={f.include_payu} onCheckedChange={(v) => set("include_payu", v)} />
            <span>Dołącz link PayU <span className="text-muted-foreground">(wymaga e-maila)</span></span>
          </label>
          <label className="flex items-center gap-2.5 text-sm cursor-pointer">
            <Switch data-testid="pf-send-email" checked={f.send_client_email} onCheckedChange={(v) => set("send_client_email", v)} />
            <span>Wyślij PDF do klienta</span>
          </label>
          <button type="submit" data-testid="pf-submit" disabled={busy} className="sm:ml-auto inline-flex items-center justify-center gap-2 bg-brand-teal hover:bg-brand-tealDark disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-full text-sm transition-colors">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />} Wystaw proformę
          </button>
        </div>
      </form>

      {result && (
        <div data-testid="proforma-generator-result" className="mt-7 border-t border-border pt-6">
          <h4 className="font-display font-bold text-base flex items-center gap-2"><CheckCircle2 size={18} className="text-brand-teal" /> Gotowe</h4>
          <p className="text-sm text-muted-foreground mt-2">Numer: <span className="font-semibold text-foreground">{result.proforma_number}</span> · do zapłaty: <span className="font-semibold text-foreground">{result.amount_label}</span></p>
          <div className="mt-4 flex flex-wrap gap-2">
            {result.documents?.map((d) => (
              <a key={d.url} href={d.url} data-testid="pf-doc" className="inline-flex items-center gap-2 text-sm font-semibold border border-border rounded-full px-4 py-2 hover:bg-muted transition-colors"><Download size={14} /> {d.name}</a>
            ))}
          </div>
          {result.pay_url && (
            <div className="mt-4">
              <label className={lbl}>Link do płatności PayU (ważny 72 h)</label>
              <div className="flex gap-2">
                <Input data-testid="pf-pay-url" readOnly value={result.pay_url} className="bg-background text-xs" />
                <button type="button" data-testid="pf-copy-pay" onClick={copyPay} className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-lg px-3 hover:bg-muted transition-colors"><Copy size={13} /> Kopiuj</button>
                <a href={result.pay_url} target="_blank" rel="noopener noreferrer" data-testid="pf-open-pay" className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold bg-brand-teal text-white rounded-lg px-3 py-2 hover:bg-brand-tealDark transition-colors">Otwórz</a>
              </div>
            </div>
          )}
          <p className="mt-4 text-xs inline-flex items-center gap-1.5 text-muted-foreground" data-testid="pf-email-status">
            {result.email_sent ? <><Send size={13} className="text-brand-teal" /> PDF wysłany do klienta.</> : "PDF nie został wysłany mailem (przełącznik wyłączony lub brak e-maila)."}
          </p>
          <p className="mt-2 text-[11px] text-muted-foreground leading-relaxed">Pobierz plik teraz — zgodnie z zasadą „zero przechowywania" rekord znika z serwera po kilku minutach (link do pobrania działa 24 h, link PayU 72 h).</p>
        </div>
      )}
    </div>
  );
}
